import { getApiBase } from './apiBase';
// ariaInstantVoice.js — HER VOICE, ALREADY DECODED (Braeden 8/16)
//
//   "how long does it take for aria to find the right audio to use? and is it
//    possible to make it as instant as possible if we have all of the audios
//    already cache"
//
// Finding it was never the problem. Measured 8/16: the bank lookup is 1.8us and
// reading the clip off disk is 40us. The whole server side is 0.05ms.
//
// The wait was the browser. `new Audio(url).play()` fetches, then DECODES mp3
// before a sound comes out - 20-100ms - and pays it again on every line, even
// one that played a second earlier. On a first impression that is the whole
// difference between a person and a slow robot.
//
// So the decode happens ONCE, at boot, for every line she owns. After that,
// speaking is creating a BufferSource and calling start(): sub-millisecond,
// no fetch, no decode, no disk, and identical on the hundredth play.
//
// The entire voice set is 2.7 MB. Holding all of her speech in memory forever
// costs less than one photo.
//
// FALLS BACK, ALWAYS. If the pack is missing, still loading, or the line is not
// in it, say() returns false and the caller does what it always did. A cache
// that can break speech is worse than no cache.

let ctx = null;            // AudioContext, made lazily on first use
let buffers = null;        // reply text -> decoded AudioBuffer
let loading = null;        // in-flight preload promise
let meta = { count: 0, bytes: 0, decoded: 0, ms: 0, failed: 0 };
let active = null;         // the one clip currently playing

function audioCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}

/**
 * Fetch the pack and decode every clip. Safe to call repeatedly - the second
 * call returns the first one's promise rather than downloading again.
 */
function _packKey() {
  try {
    const src = [...document.scripts].map((s) => s.src || '').find((u) => /index-[\w-]+\.js/.test(u)) || '';
    return (src.match(/index-([\w-]+)\.js/) || [])[1] || String(Math.floor(Date.now() / 3600000));
  } catch (_) { return String(Math.floor(Date.now() / 3600000)); }
}

export function preload(base = '/aria-voice') {
  if (loading) return loading;
  loading = (async () => {
    const t0 = (performance && performance.now) ? performance.now() : Date.now();
    const ac = audioCtx();
    if (!ac) return false;
    let man, bin;
    try {
      const [mr, br] = await Promise.all([
        // Keyed to the bundle hash (9/3): force-cache served a pack from
        // before today's lines were banked, so the drawer line fell back to
        // the slow voice while the served pack already had it. A new build
        // means a new key, so the browser refetches exactly when it should.
        fetch(`${base}/voicepack.json?v=${_packKey()}`, { cache: 'force-cache' }),
        fetch(`${base}/voicepack.bin?v=${_packKey()}`, { cache: 'force-cache' }),
      ]);
      if (!mr.ok || !br.ok) return false;
      man = await mr.json();
      bin = await br.arrayBuffer();
    } catch (_) { return false; }

    // ── THE PACK MUST BE IN HER CURRENT VOICE (8/25) ──────────────────────
    // Braeden: "its not updated, calls on an old voice ... aria only has one
    // consistant voice."
    //
    // This pack is 152 pre-recorded mp3 clips, baked 2026-08-16, that play
    // through a BufferSource - it never touches /tts. So when her voice
    // changed, the /tts guard and the OpenAI client wrapper both kept every
    // other path honest and THIS one kept speaking in the old one. Common
    // lines like "All good. I'm here." came out in a voice nine days stale
    // while everything else was current: two voices in one conversation, and
    // no amount of fixing /tts could ever have caught it.
    //
    // A pack is now only usable if it says which voice it was baked in and
    // that matches what the server is speaking. An unstamped pack (every pack
    // built before today) is refused, so those lines fall through to /tts and
    // sound like her. Rebuild it stamped and the instant path comes straight
    // back.
    try {
      const hr = await fetch(`${getApiBase() || ''}/api/health`, { cache: 'no-store' });
      const serverVoice = hr.ok ? ((await hr.json()) || {}).voice : null;
      if (!serverVoice || man.voice !== serverVoice) {
        console.warn('[instantVoice] pack voice', JSON.stringify(man.voice || null),
          '!= server voice', JSON.stringify(serverVoice), '- not using the pack, every line goes to /tts');
        meta = { count: 0, bytes: 0, decoded: 0, ms: 0, failed: 0, refused: 'voice-mismatch' };
        buffers = new Map();
        return false;
      }
    } catch (_) {
      buffers = new Map();
      return false; // Unverified packs must not substitute an old voice.
    }

    const out = new Map();
    let failed = 0;
    // decodeAudioData DETACHES the buffer it is given, so each clip must get
    // its own copy - slicing the shared ArrayBuffer in place would leave every
    // later clip reading from a zero-length buffer.
    await Promise.all(Object.entries(man.index || {}).map(async ([reply, [off, len]]) => {
      try {
        const slice = bin.slice(off, off + len);
        const buf = await ac.decodeAudioData(slice);
        out.set(reply, buf);
      } catch (_) { failed++; }
    }));

    buffers = out;
    meta = {
      count: man.count || 0, bytes: man.bytes || 0, decoded: out.size, failed,
      ms: Math.round(((performance && performance.now) ? performance.now() : Date.now()) - t0),
    };
    return true;
  })();
  return loading;
}

/** Is this exact line already decoded and ready? */
export function has(text) {
  return !!(buffers && buffers.get(String(text || '')));
}

// ── THE CONTEXT MUST BE UNLOCKED BY A REAL CLICK (8/19) ────────────
// preload() runs at boot, before any gesture, so the AudioContext is born
// SUSPENDED. Chrome only honours resume() while a user gesture is on the call
// stack — and say() runs from fetch callbacks, where resume() is silently
// ignored. Result, measured 8/19 in the live app: say() started a source on a
// frozen clock, returned true, the UI showed "speaking", and zero samples
// reached the speakers. Braeden: "it says speaking it just doesnt talk just
// kinda sits there."
//
// unlock() is the missing half: called from a real pointer/key event (App.jsx
// wires it to the first gesture), resume() is allowed, and every say() after
// that is genuinely instant.
export function unlock() {
  const ac = audioCtx();
  if (ac && ac.state === 'suspended') { try { ac.resume(); } catch (_) {} }
}

/**
 * Speak a banked line instantly. Returns false when it cannot, so the caller
 * falls through to the normal /tts path unchanged.
 * @param {object} o  onended, rate (0.7-1.4)
 */
export function say(text, o = {}) {
  const buf = buffers && buffers.get(String(text || ''));
  if (!buf) return false;
  const ac = audioCtx();
  if (!ac) return false;
  // A context created before any user gesture starts suspended. resume() is a
  // no-op once running, and the gesture that triggered this turn has already
  // happened, so this is the moment it is allowed to start.
  if (ac.state === 'suspended') { try { ac.resume(); } catch (_) {} }
  // If it is STILL suspended, the resume above was ignored (no gesture on the
  // stack) and any source we start now plays into a frozen clock. Claiming
  // success here is how she "speaks" in silence — hand the line to the /tts
  // <audio> path instead, which the browser will actually play.
  if (ac.state === 'suspended') return false;

  // ONE AT A TIME. She must never talk over herself - the same rule the /tts
  // path enforces, and easy to lose when playback gets this cheap.
  if (active) { try { active.stop(); } catch (_) {} active = null; }

  const src = ac.createBufferSource();
  src.buffer = buf;
  const rate = Number(o.rate);
  if (rate >= 0.7 && rate <= 1.4) src.playbackRate.value = rate;
  src.connect(ac.destination);

  // ── onended MUST FIRE, OR SHE GOES DEAF (Braeden 8/16) ────────────────
  //
  //   "again i answer her and she doesnt respond why?"
  //
  // The caller sets ariaSpeaking=true around this call and clears it in
  // onended. The app has an ECHO GATE that throws away any transcript arriving
  // while ariaSpeaking is true — correct, or she replies to her own voice in a
  // loop. But if onended never fires, that flag is stuck true FOREVER and every
  // single thing he says is silently binned. He said the words, whisper heard
  // them, and she sat there.
  //
  // It can genuinely never fire: this AudioContext is built at page load,
  // BEFORE any click, so browsers create it suspended. start(0) on a suspended
  // context schedules against a clock that is not running — no sound, no
  // 'ended'. My own test missed this because I made the context after clicking,
  // which is exactly the state a real first load is not in.
  //
  // So: resume first, and back the event with a timer sized to the clip. One of
  // the two always fires, and whichever is first wins.
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    if (active === src) active = null;
    if (o.onended) { try { o.onended(); } catch (_) {} }
  };
  src.onended = finish;
  active = src;

  const go = () => { try { src.start(0); } catch (_) { finish(); return false; } return true; };

  if (ac.state === 'suspended') {
    // Do not start until the clock is actually running, or the clip is
    // scheduled into a paused timeline and never plays.
    let started = false;
    try {
      const p = ac.resume();
      if (p && typeof p.then === 'function') {
        p.then(() => { if (!started) { started = true; go(); } })
         .catch(() => { finish(); });
      } else { started = true; go(); }
    } catch (_) { finish(); return false; }
    // If resume() never settles (it can hang until a gesture arrives), release
    // the flag anyway so the echo gate reopens. Better a missed clip than a
    // deaf assistant.
    setTimeout(() => { if (!started) finish(); }, 1500);
  } else if (!go()) return false;

  // duration + a small tail, scaled by playback rate
  const ms = (buf.duration / (src.playbackRate.value || 1)) * 1000 + 400;
  setTimeout(finish, Math.min(30000, Math.max(500, ms)));
  // The clip's real length (ms, at this rate), so the caller can time a
  // ring or a card to the PHRASE instead of the start of the sentence
  // (Braeden 9/2: "when she's talking, the timing has to be right").
  // Truthy, so every `if (say(...))` caller keeps working unchanged.
  return Math.max(1, Math.round(ms - 400));
}

export function stop() {
  if (active) { try { active.stop(); } catch (_) {} active = null; }
}

/** For the tracker and for proving the claim rather than asserting it. */
export function stats() {
  return { ...meta, ready: !!buffers, playing: !!active };
}
